import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './Payroll.css';

const Payroll = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    payrollPeriod: new Date().toISOString().slice(0, 7), // YYYY-MM
    payrollDate: new Date().toISOString().split('T')[0],
    baseSalary: 0,
    daysWorked: 30,
    hoursWorked: 240
  });

  // Obtener nóminas
  const { data, isLoading, error } = useQuery({
    queryKey: ['payrolls', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/payroll`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Obtener empleados
  const { data: employees } = useQuery({
    queryKey: ['employees', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/employees`);
      return response.data.data.employees || [];
    },
    enabled: !!companyId
  });

  // Crear nómina
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/payroll`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payrolls', companyId]);
      setShowForm(false);
      setFormData({
        employeeId: '',
        payrollPeriod: new Date().toISOString().slice(0, 7),
        payrollDate: new Date().toISOString().split('T')[0],
        baseSalary: 0,
        daysWorked: 30,
        hoursWorked: 240
      });
    }
  });

  // Aprobar nómina
  const approveMutation = useMutation({
    mutationFn: async (payrollId) => {
      const response = await api.post(`/companies/${companyId}/payroll/${payrollId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payrolls', companyId]);
      alert('Nómina aprobada exitosamente');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  if (isLoading) return <div>Cargando nóminas...</div>;
  if (error) return <div>Error al cargar nóminas</div>;

  return (
    <div className="payroll">
      <div className="page-header">
        <h2>Nómina</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nueva Nómina'}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Nueva Nómina</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Empleado *</label>
                <select
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  required
                >
                  <option value="">Seleccionar...</option>
                  {employees?.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} - {emp.nit}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Período *</label>
                <input
                  type="month"
                  value={formData.payrollPeriod}
                  onChange={(e) => setFormData({ ...formData, payrollPeriod: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha de Pago *</label>
                <input
                  type="date"
                  value={formData.payrollDate}
                  onChange={(e) => setFormData({ ...formData, payrollDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Salario Base *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.baseSalary}
                  onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Días Trabajados</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={formData.daysWorked}
                  onChange={(e) => setFormData({ ...formData, daysWorked: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Horas Trabajadas</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hoursWorked}
                  onChange={(e) => setFormData({ ...formData, hoursWorked: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                {createMutation.isLoading ? 'Calculando...' : 'Calcular y Crear Nómina'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="payroll-list">
        <h3>Lista de Nóminas ({data?.pagination?.total || 0})</h3>
        {data?.payrolls && data.payrolls.length > 0 ? (
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Período</th>
                <th>Empleado</th>
                <th>Salario Base</th>
                <th>Bruto</th>
                <th>Neto</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.payrolls.map(payroll => (
                <tr key={payroll.id}>
                  <td>{payroll.payrollPeriod}</td>
                  <td>{payroll.employee?.name}</td>
                  <td>${payroll.baseSalary.toFixed(2)}</td>
                  <td>${payroll.grossSalary.toFixed(2)}</td>
                  <td>${payroll.netSalary.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${payroll.status}`}>
                      {payroll.status}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => setSelectedPayroll(payroll)}
                      className="btn-link"
                    >
                      Ver
                    </button>
                    {payroll.status === 'calculado' && (
                      <button
                        onClick={() => approveMutation.mutate(payroll.id)}
                        className="btn-link"
                        disabled={approveMutation.isLoading}
                      >
                        Aprobar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay nóminas registradas</p>
          </div>
        )}
      </div>

      {selectedPayroll && (
        <div className="modal-overlay" onClick={() => setSelectedPayroll(null)}>
          <div className="modal-content payroll-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle de Nómina</h3>
              <button onClick={() => setSelectedPayroll(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="payroll-summary">
                <h4>Resumen</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <strong>Empleado:</strong> {selectedPayroll.employee?.name}
                  </div>
                  <div className="summary-item">
                    <strong>Período:</strong> {selectedPayroll.payrollPeriod}
                  </div>
                  <div className="summary-item">
                    <strong>Salario Base:</strong> ${selectedPayroll.baseSalary.toFixed(2)}
                  </div>
                  <div className="summary-item">
                    <strong>Salario Bruto:</strong> ${selectedPayroll.grossSalary.toFixed(2)}
                  </div>
                  <div className="summary-item">
                    <strong>Salario Neto:</strong> ${selectedPayroll.netSalary.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="deductions-section">
                <h4>Deducciones</h4>
                {selectedPayroll.deductions?.breakdown && (
                  <table className="detail-table">
                    <tbody>
                      <tr>
                        <td>EPS (4%):</td>
                        <td>${selectedPayroll.deductions.breakdown.eps?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Pensión (4%):</td>
                        <td>${selectedPayroll.deductions.breakdown.pension?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>ARL:</td>
                        <td>${selectedPayroll.deductions.breakdown.arl?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Retención en la Fuente:</td>
                        <td>${selectedPayroll.deductions.breakdown.retentionInSource?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr className="total-row">
                        <td><strong>Total Deducciones:</strong></td>
                        <td><strong>${selectedPayroll.deductions?.total.toFixed(2) || 0}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              <div className="parafiscals-section">
                <h4>Parafiscales (Empresa)</h4>
                {selectedPayroll.parafiscals?.breakdown && (
                  <table className="detail-table">
                    <tbody>
                      <tr>
                        <td>SENA (2%):</td>
                        <td>${selectedPayroll.parafiscals.breakdown.sena?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>ICBF (3%):</td>
                        <td>${selectedPayroll.parafiscals.breakdown.icbf?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Cajas de Compensación (4%):</td>
                        <td>${selectedPayroll.parafiscals.breakdown.cajas?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr className="total-row">
                        <td><strong>Total Parafiscales:</strong></td>
                        <td><strong>${selectedPayroll.parafiscals?.total.toFixed(2) || 0}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              <div className="provisions-section">
                <h4>Provisiones</h4>
                {selectedPayroll.provisions?.breakdown && (
                  <table className="detail-table">
                    <tbody>
                      <tr>
                        <td>Vacaciones (4.17%):</td>
                        <td>${selectedPayroll.provisions.breakdown.vacations?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Cesantías (8.33%):</td>
                        <td>${selectedPayroll.provisions.breakdown.severance?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Intereses de Cesantías (1%):</td>
                        <td>${selectedPayroll.provisions.breakdown.severanceInterest?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr>
                        <td>Prima de Servicios (4.17%):</td>
                        <td>${selectedPayroll.provisions.breakdown.bonus?.amount.toFixed(2) || 0}</td>
                      </tr>
                      <tr className="total-row">
                        <td><strong>Total Provisiones:</strong></td>
                        <td><strong>${selectedPayroll.provisions?.total.toFixed(2) || 0}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;

